/**
 * Autocompletar de la contraparte — "a quién le pagué / de quién cobré" (spec aiChatBot
 * 2026-10-17-registro-sobre-nota-y-contraparte §2.3). Puro.
 *
 * Busca en las CUENTAS de la empresa (nombre, apellido, email), en las PERSONAS del campo
 * (personas_campo, sin cuenta) y en los APODOS de unas y otras ("Juancho" → Juan Gómez,
 * "vete" → el sujeto Veterinario). Prefijo por palabra, sin acentos ni mayúsculas.
 */
import type { PersonaCampo } from '../storage/interfaces/IPersonaCampoRepository';
import type { Apodo } from '../storage/interfaces/IApodoRepository';
import type { ContraparteCampos } from '../../types/administracion';

export interface CuentaEmpresa {
  user_id: string;
  nombre?: string | null;
  apellido?: string | null;
  email?: string | null;
}

export interface OpcionContraparte {
  tipo: 'usuario' | 'persona';
  id: string;
  nombre: string;
  rol?: string | null;
  /** Apodo por el que matcheó (para mostrar "Juan Gómez · Juancho"). */
  via?: string;
}

export interface ContextoContraparte {
  cuentas: CuentaEmpresa[];
  personas: PersonaCampo[];
  apodos: Pick<Apodo, 'tipo_entidad' | 'entidad_id' | 'apodo' | 'is_deleted'>[] & unknown[];
}

const norm = (s: string | null | undefined) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().trim();

const palabras = (s: string) => norm(s).split(/[^a-z0-9@._-]+/).filter(Boolean);

/** Cada palabra de la consulta es prefijo de alguna palabra del texto. */
function matchea(q: string[], texto: string): boolean {
  const ws = palabras(texto);
  return q.length > 0 && q.every((p) => ws.some((w) => w.startsWith(p)));
}

export const nombreCuenta = (c: CuentaEmpresa) =>
  [c.nombre, c.apellido].filter(Boolean).join(' ').trim() || c.email || 'Usuario';

export function buscarContrapartes(consulta: string, ctx: ContextoContraparte, limite = 8): OpcionContraparte[] {
  const q = palabras(consulta);
  if (!q.length) return [];
  const apodosDe = (tipo: 'usuario' | 'persona', id: string) =>
    (ctx.apodos as Apodo[]).filter((a) => !a.is_deleted && a.tipo_entidad === tipo && String(a.entidad_id) === String(id)).map((a) => a.apodo);

  const out: OpcionContraparte[] = [];
  for (const c of ctx.cuentas || []) {
    const nombre = nombreCuenta(c);
    const via = apodosDe('usuario', c.user_id).find((a) => matchea(q, a));
    if (matchea(q, `${nombre} ${c.email || ''}`) || via) out.push({ tipo: 'usuario', id: c.user_id, nombre, ...(via && !matchea(q, nombre) ? { via } : {}) });
  }
  for (const p of ctx.personas || []) {
    if (p.is_deleted) continue;
    const via = apodosDe('persona', p.id).find((a) => matchea(q, a));
    if (matchea(q, p.nombre) || via) out.push({ tipo: 'persona', id: p.id, nombre: p.nombre, rol: p.rol ?? null, ...(via && !matchea(q, p.nombre) ? { via } : {}) });
  }
  return out.slice(0, limite);
}

/** Opción elegida (o nada) → columnas contraparte_* para guardar. */
export function contraparteDeOpcion(o: Pick<OpcionContraparte, 'tipo' | 'id' | 'nombre'> | null): Required<ContraparteCampos> {
  if (!o) return { contraparte_user_id: null, contraparte_persona_id: null, contraparte_nombre: null };
  return {
    contraparte_user_id: o.tipo === 'usuario' ? o.id : null,
    contraparte_persona_id: o.tipo === 'persona' ? o.id : null,
    contraparte_nombre: o.nombre,
  };
}

/**
 * Sólo las columnas contraparte_* que traen valor (para los records que viajan por sync):
 * sin contraparte no se manda ninguna key — una base todavía sin la migración 20261018100000
 * rechazaría el upsert entero por una columna desconocida.
 */
export function contraparteConValor(c: ContraparteCampos | null | undefined): ContraparteCampos {
  if (!c) return {};
  const out: ContraparteCampos = {};
  if (c.contraparte_user_id) out.contraparte_user_id = c.contraparte_user_id;
  if (c.contraparte_persona_id) out.contraparte_persona_id = c.contraparte_persona_id;
  if (c.contraparte_nombre) out.contraparte_nombre = c.contraparte_nombre;
  return out;
}
